/* Filazoo popup system — shared cursor-follow close for every overlay.

   One controller, one cursor element, one document-level pointer listener that
   is only attached while a popup is actually open. Popups register themselves
   on open and drop out on close; nothing polls and nothing observes the DOM.

   The affordance is mouse-only. On a coarse pointer the cursor is never built
   and every popup falls back to the close button it already ships. */

(function () {
  'use strict';

  var CURSOR_MARKUP =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round"/></svg>';

  /* What counts as the scrim. Matched exactly, never by ancestry: an overlay is
     either the registered root itself (the pattern Dawn's modals and the native
     <dialog> both use, where the element fills the viewport and the panel is a
     child) or a dedicated scrim element listed here.

     The looser test — "anything not inside the panel" — is wrong. A popup's own
     toggle button, close button and header chrome all sit outside the panel box,
     so that test would treat a click on them as a scrim dismissal and race the
     theme's own handler, which in the worst case toggles the popup straight back
     open. Exact matching is also what the video reviews dialog did originally. */
  var SCRIM_SELECTOR = [
    '.cart-drawer__overlay',
    '.filazoo-need-to-know__backdrop',
    '.homepage-newsletter-popup__overlay',
    '.bambu-search__overlay'
  ].join(',');

  var finePointer =
    window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  var cursor = null;
  var active = null; /* { root, host, close } */
  var frame = 0;
  var pending = null;
  var listening = false;

  function build() {
    if (cursor) return cursor;
    cursor = document.createElement('span');
    cursor.className = 'filazoo-popup-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = CURSOR_MARKUP;
    return cursor;
  }

  /* A native <dialog> paints in the top layer, above anything parented to
     <body>, so for those the cursor has to live inside the dialog. Its
     transform then makes it the containing block, hence the anchored mode. */
  function hostFor(root) {
    return root && root.tagName === 'DIALOG' ? root : document.body;
  }

  function render() {
    frame = 0;
    if (!pending || !active || !cursor) return;
    var x = pending.x;
    var y = pending.y;
    if (active.host !== document.body) {
      var rect = active.host.getBoundingClientRect();
      x -= rect.left;
      y -= rect.top;
    }
    cursor.style.transform =
      'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
  }

  function schedule(x, y) {
    pending = { x: x, y: y };
    if (frame) return;
    frame = window.requestAnimationFrame(render);
  }

  function show(on) {
    if (!cursor || !active) return;
    cursor.classList.toggle('is-active', on);
    if (!on) cursor.classList.remove('is-pressed');
    active.root.classList.toggle('filazoo-popup-scrim-hidden', on);
  }

  function outside(target) {
    if (!active || !(target instanceof Element)) return false;
    if (target === active.root) return true;
    return target.matches(SCRIM_SELECTOR);
  }

  function onMove(event) {
    if (!active) return;
    var over = outside(event.target);
    if (over) schedule(event.clientX, event.clientY);
    show(over);
  }

  function onDown(event) {
    if (!active || !cursor) return;
    if (outside(event.target)) cursor.classList.add('is-pressed');
  }

  function onUp() {
    if (cursor) cursor.classList.remove('is-pressed');
  }

  function onClick(event) {
    if (!active) return;
    if (!outside(event.target)) return;
    var close = active.close;
    if (typeof close === 'function') close();
  }

  function listen(on) {
    if (on === listening) return;
    listening = on;
    var method = on ? 'addEventListener' : 'removeEventListener';
    document[method]('pointermove', onMove, true);
    document[method]('pointerdown', onDown, true);
    document[method]('pointerup', onUp, true);
    document[method]('click', onClick, true);
  }

  /* API -------------------------------------------------------------------- */

  function activate(root, close) {
    if (!finePointer || !root) return;
    deactivate();
    var el = build();
    var host = hostFor(root);
    if (el.parentNode !== host) host.appendChild(el);
    el.classList.toggle('is-anchored', host !== document.body);
    el.classList.remove('is-active', 'is-pressed');
    active = { root: root, host: host, close: close };
    listen(true);
  }

  function deactivate(root) {
    if (!active) return;
    if (root && root !== active.root) return;
    show(false);
    active.root.classList.remove('filazoo-popup-scrim-hidden');
    active = null;
    pending = null;
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
    listen(false);
  }

  window.FilazooPopup = {
    activate: activate,
    deactivate: deactivate,
    get supported() {
      return finePointer;
    }
  };
})();
